import type { WorkspaceWithRole } from "@workspace/api-client-react";

export interface WorkspaceBranding {
  companyName?: string;
  accentColor?: string;
  logo?: { storageKey: string };
  icon?: { storageKey: string };
}

export function workspaceDisplayName(
  workspace: Pick<WorkspaceWithRole, "name" | "branding">,
): string {
  return workspace.branding?.companyName?.trim() || workspace.name;
}

export function hexToHslTriplet(hex: string): string | null {
  const normalized = hex.replace("#", "").trim();
  if (!/^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(normalized)) return null;

  const expand = normalized.length === 3
    ? normalized.split("").map((c) => c + c).join("")
    : normalized;

  const r = parseInt(expand.slice(0, 2), 16) / 255;
  const g = parseInt(expand.slice(2, 4), 16) / 255;
  const b = parseInt(expand.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = ((g - b) / delta) % 6;
        break;
      case g:
        h = (b - r) / delta + 2;
        break;
      default:
        h = (r - g) / delta + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * "H S% L%" with the percent signs REQUIRED and every channel in range.
 *
 * The percent used to be optional here and server-side, which let a workspace
 * store "250 85 50". That is not valid CSS, so `hsl(250 85 50)` made the browser
 * drop the whole declaration and the workspace silently had NO accent at all —
 * indistinguishable from "branding is broken". Reject instead, so a bad value
 * falls back to OmniMedia deliberately rather than by CSS parse failure.
 */
const HSL_TRIPLET_RE =
  /^(\d{1,3}(?:\.\d+)?)\s+(\d{1,3}(?:\.\d+)?)%\s+(\d{1,3}(?:\.\d+)?)%$/;

export interface HslTriplet {
  h: number;
  s: number;
  l: number;
}

export function parseHslTriplet(value: string): HslTriplet | null {
  const m = HSL_TRIPLET_RE.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const s = Number(m[2]);
  const l = Number(m[3]);
  if (h > 360 || s > 100 || l > 100) return null;
  return { h, s, l };
}

/**
 * The accent as a canonical CSS triplet, or null when it cannot be rendered.
 *
 * Canonicalising (rather than echoing the input) matters because the result is
 * persisted: "250  85%   50%" and "250 85% 50%" must not be two different
 * stored values for the same colour.
 */
export function accentColorToPrimaryTriplet(accentColor?: string): string | null {
  if (!accentColor?.trim()) return null;
  const trimmed = accentColor.trim();
  if (trimmed.startsWith("#")) return hexToHslTriplet(trimmed);
  const parsed = parseHslTriplet(trimmed);
  return parsed ? formatHslTriplet(parsed) : null;
}

function formatHslTriplet({ h, s, l }: HslTriplet): string {
  return `${h} ${s}% ${l}%`;
}

function hslToRgb({ h, s, l }: HslTriplet): [number, number, number] {
  const sat = s / 100;
  const lig = l / 100;
  const c = (1 - Math.abs(2 * lig - 1)) * sat;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = lig - c / 2;
  const [r, g, b] =
    hp < 1 ? [c, x, 0]
    : hp < 2 ? [x, c, 0]
    : hp < 3 ? [0, c, x]
    : hp < 4 ? [0, x, c]
    : hp < 5 ? [x, 0, c]
    : [c, 0, x];
  return [r + m, g + m, b + m];
}

/** WCAG 2.1 relative luminance from sRGB channels already in 0..1. */
function relativeLuminance([r, g, b]: [number, number, number]): number {
  const lin = (channel: number) =>
    channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrastRatio(a: number, b: number): number {
  const [hi, lo] = a >= b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

/** Matches --primary-foreground / --sidebar-primary-foreground in index.css. */
export const LIGHT_FOREGROUND_TRIPLET = "0 0% 100%";
/** Matches --foreground in index.css. */
export const DARK_FOREGROUND_TRIPLET = "240 10% 10%";

/**
 * Text colour to sit ON the accent, chosen by WCAG contrast.
 *
 * A lightness threshold is not good enough: pure yellow is L=50 yet needs black
 * text. Going through relative luminance is what keeps a pale accent readable.
 * The product default (250 85% 50%) resolves to white, which is byte-identical
 * to the index.css default, so an unbranded workspace sees no change at all.
 */
export function accentForegroundTriplet(accentTriplet: string): string {
  const parsed = parseHslTriplet(accentTriplet);
  if (!parsed) return LIGHT_FOREGROUND_TRIPLET;
  const accentLuminance = relativeLuminance(hslToRgb(parsed));
  const onLight = contrastRatio(
    accentLuminance,
    relativeLuminance(hslToRgb(parseHslTriplet(LIGHT_FOREGROUND_TRIPLET)!)),
  );
  const onDark = contrastRatio(
    accentLuminance,
    relativeLuminance(hslToRgb(parseHslTriplet(DARK_FOREGROUND_TRIPLET)!)),
  );
  return onDark > onLight ? DARK_FOREGROUND_TRIPLET : LIGHT_FOREGROUND_TRIPLET;
}

export interface ResolvedAccent {
  /** Canonical "H S% L%" for the accent tokens. */
  accent: string;
  /** Canonical "H S% L%" for the text that sits on the accent. */
  foreground: string;
}

/** Parse + validate + derive, in one step. Null when the accent is unusable. */
export function resolveAccent(accentColor?: string): ResolvedAccent | null {
  const accent = accentColorToPrimaryTriplet(accentColor);
  if (!accent) return null;
  return { accent, foreground: accentForegroundTriplet(accent) };
}

export function hasBrandingAsset(
  branding: WorkspaceBranding | undefined,
  kind: "logo" | "icon",
): boolean {
  return Boolean(kind === "logo" ? branding?.logo : branding?.icon);
}

// Presigned branding URLs are valid for 1h server-side; refetch a little early.
export const BRANDING_URL_STALE_TIME_MS = 50 * 60 * 1000;

/**
 * Root CSS custom properties a workspace accent overrides. Beyond `--primary`
 * (buttons/links) the sidebar accent tokens are included so a branded workspace
 * is consistent across the WHOLE signed-in app, not just the main content area
 * (the sidebar previously stayed OmniMedia indigo — CB-149). Full white-label
 * (charts, favicon, page title, sign-in card) is deliberately still deferred.
 */
export const BRANDED_ACCENT_VARS = [
  "--primary",
  "--ring",
  "--sidebar-primary",
  "--sidebar-ring",
] as const;

/**
 * Text-on-accent tokens. Separate from BRANDED_ACCENT_VARS because they take a
 * DIFFERENT value (the derived foreground, not the accent), and clearing has to
 * cover both — hence ALL_BRANDED_VARS below.
 */
export const BRANDED_ACCENT_FOREGROUND_VARS = [
  "--primary-foreground",
  "--sidebar-primary-foreground",
] as const;

export const ALL_BRANDED_VARS = [
  ...BRANDED_ACCENT_VARS,
  ...BRANDED_ACCENT_FOREGROUND_VARS,
] as const;

/**
 * Apply / clear the workspace accent on `document.documentElement`.
 *
 * These write GLOBAL state, so they have exactly ONE owner:
 * components/branding/workspace-accent-controller.tsx. Two components used to
 * call them through useWorkspaceBranding(), and because that hook cleared on
 * unmount, leaving any page that rendered AppLayout stripped the accent
 * app-wide while the other owner sat there with an unchanged dependency and
 * never re-applied. An ESLint rule now pins the ownership; registerAccentOwner
 * below catches a second owner at runtime in dev.
 *
 * Kept out of the React hook so a sign-out handler can clear branding
 * synchronously and imperatively (CB-149). No-ops outside the browser.
 */
export function applyWorkspaceAccent(resolved: ResolvedAccent): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const v of BRANDED_ACCENT_VARS) root.style.setProperty(v, resolved.accent);
  for (const v of BRANDED_ACCENT_FOREGROUND_VARS) {
    root.style.setProperty(v, resolved.foreground);
  }
}

export function clearWorkspaceAccent(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  for (const v of ALL_BRANDED_VARS) root.style.removeProperty(v);
}

let accentOwnerCount = 0;

/**
 * Dev-only duplicate-owner detector. Call from the one component allowed to
 * write the accent vars; the returned function releases the claim.
 *
 * The onDuplicate callback is injectable so this is testable without a renderer
 * (there is no DOM test infrastructure anywhere in this workspace).
 */
export function registerAccentOwner(
  onDuplicate: (count: number) => void = defaultDuplicateOwnerWarning,
): () => void {
  accentOwnerCount += 1;
  if (accentOwnerCount > 1) onDuplicate(accentOwnerCount);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    accentOwnerCount -= 1;
  };
}

function defaultDuplicateOwnerWarning(count: number): void {
  if (!import.meta.env?.DEV) return;
  console.error(
    `[branding] ${count} components are writing the workspace accent CSS variables. ` +
      "There must be exactly one (components/branding/workspace-accent-controller.tsx): " +
      "a second owner's unmount clears the accent app-wide and it is never re-applied.",
  );
}
